import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import PageHeader from '../../components/common/PageHeader';
import FilterCard from '../../components/common/FilterCard';
import { useI18n } from '../../i18n/I18nContext';
import { useFilter } from '../../context/FilterContext';
import remorqueService from '../../services/remorqueService';

function RemorqueList() {
    const [remorques, setRemorques] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [hoveredId, setHoveredId] = useState(null);
    const navigate = useNavigate();
    const { t } = useI18n();

    const { selectedTransporteurId } = useFilter();

    useEffect(() => {
        fetchRemorques();
    }, [selectedTransporteurId]);

    const fetchRemorques = async () => {
        try {
            setLoading(true);
            const data = await remorqueService.getRemorques(selectedTransporteurId);
            setRemorques(data);
            setError(null);
        } catch (err) {
            setError(err.message || t('remorques.errors.loadFailed'));
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm(t('remorques.confirmDelete'))) {
            try {
                await remorqueService.deleteRemorque(id);
                setRemorques(remorques.filter((r) => r.id !== id));
            } catch (err) {
                alert(err.message || t('remorques.errors.deleteFailed'));
            }
        }
    };

    const handleStatusChange = async (id, newStatus) => {
        try {
            await remorqueService.updateRemorqueStatus(id, newStatus);
            fetchRemorques(); // Refresh to get enriched metadata
        } catch (err) {
            alert(err.message || t('remorques.errors.updateFailed'));
        }
    };

    const filteredRemorques = remorques.filter((r) => {
        const matchesSearch = (
            r.plateNumber?.toLowerCase().includes(search.toLowerCase()) ||
            r.brand?.toLowerCase().includes(search.toLowerCase()) ||
            r.model?.toLowerCase().includes(search.toLowerCase()) ||
            r.type?.toLowerCase().includes(search.toLowerCase())
        );
        const matchesStatus = statusFilter === '' || r.status === statusFilter;
        return matchesSearch && matchesStatus;
    });

    const getStatusBadgeClass = (status) => {
        switch (status) {
            case 'AVAILABLE': return 'bg-success';
            case 'IN_USE': return 'bg-primary';
            case 'MAINTENANCE': return 'bg-warning text-dark';
            case 'OUT_OF_SERVICE': return 'bg-danger';
            default: return 'bg-secondary';
        }
    };

    const getStatusTranslationKey = (status) => {
        switch (status) {
            case 'AVAILABLE': return 'available';
            case 'IN_USE': return 'inUse';
            case 'MAINTENANCE': return 'inMaintenance';
            case 'OUT_OF_SERVICE': return 'unavailable';
            default: return status?.toLowerCase() || '';
        }
    };

    return (
        <DashboardLayout>
            <div className="container-fluid py-4">
                <PageHeader
                    title={t('remorques.title')}
                    subtitle={t('remorques.subtitle')}
                    actions={
                        <button
                            className="btn btn-primary d-flex align-items-center gap-2"
                            onClick={() => navigate('/remorques/create')}
                        >
                            <i className="bi bi-plus-lg"></i>
                            {t('remorques.addNew')}
                        </button>
                    }
                />

                <FilterCard>
                    <div className="row g-3">
                        <div className="col-md-8">
                            <div className="input-group">
                                <span className="input-group-text bg-white border-end-0">
                                    <i className="bi bi-search text-muted"></i>
                                </span>
                                <input
                                    type="text"
                                    className="form-control border-start-0 ps-0"
                                    placeholder={t('remorques.searchPlaceholder')}
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="col-md-4">
                            <select
                                className="form-select"
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                            >
                                <option value="">{t('remorques.allStatus')}</option>
                                <option value="AVAILABLE">{t('remorques.status.available')}</option>
                                <option value="IN_USE">{t('remorques.status.inUse')}</option>
                                <option value="MAINTENANCE">{t('remorques.status.inMaintenance')}</option>
                                <option value="OUT_OF_SERVICE">{t('remorques.status.unavailable')}</option>
                            </select>
                        </div>
                    </div>
                </FilterCard>

                {error && (
                    <div className="alert alert-danger d-flex align-items-center mb-4" role="alert">
                        <i className="bi bi-exclamation-triangle-fill me-2"></i>
                        {error}
                    </div>
                )}

                <div className="card border-0 shadow-sm">
                    <div className="card-body p-0">
                        <div className="table-responsive">
                            <table className="table table-hover align-middle mb-0">
                                <thead className="bg-light text-muted small text-uppercase">
                                    <tr>
                                        <th className="px-4 py-3">{t('remorques.table.plateNumber')}</th>
                                        <th className="py-3">{t('remorques.table.brand')} / {t('remorques.table.model')}</th>
                                        <th className="py-3">{t('remorques.table.type')}</th>
                                        <th className="py-3">{t('remorques.table.capacity')}</th>
                                        <th className="py-3">{t('remorques.table.status')}</th>
                                        <th className="px-4 py-3 text-end">{t('remorques.table.actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan="6" className="text-center py-5">
                                                <div className="spinner-border text-primary" role="status">
                                                    <span className="visually-hidden">{t('common.loading')}</span>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : filteredRemorques.length === 0 ? (
                                        <tr>
                                            <td colSpan="6" className="text-center py-5">
                                                <i className="bi bi-truck-flatbed text-muted mb-2 d-block" style={{ fontSize: '3rem' }}></i>
                                                <p className="text-muted mb-0">{t('remorques.noRemorquesFound')}</p>
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredRemorques.map((remorque) => (
                                            <tr
                                                key={remorque.id}
                                                onMouseEnter={() => setHoveredId(remorque.id)}
                                                onMouseLeave={() => setHoveredId(null)}
                                            >
                                                <td className="px-4">
                                                    <span className="fw-bold text-dark">{remorque.plateNumber}</span>
                                                </td>
                                                <td>
                                                    <div className="d-flex flex-column">
                                                        <span className="text-dark">{remorque.brand}</span>
                                                        <small className="text-muted">{remorque.model}</small>
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className="badge bg-light text-dark border">
                                                        {t(`remorques.types.${remorque.type?.toLowerCase()}`)}
                                                    </span>
                                                </td>
                                                <td>
                                                    {remorque.capacity ? `${remorque.capacity} t` : '-'}
                                                </td>
                                                <td>
                                                    <div className="d-flex flex-column gap-1">
                                                        <span className={`badge rounded-pill ${getStatusBadgeClass(remorque.status)}`}>
                                                            {t(`remorques.status.${getStatusTranslationKey(remorque.status)}`)}
                                                        </span>
                                                        {remorque.assignedMissionInfo && (
                                                            <small className="text-muted d-flex align-items-center gap-1">
                                                                <i className="bi bi-link-45deg"></i>
                                                                {remorque.assignedMissionInfo}
                                                            </small>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-4 text-end">
                                                    <div className={`d-flex justify-content-end gap-2 transition-opacity ${hoveredId === remorque.id ? 'opacity-100' : 'opacity-0 opacity-md-100'}`}>
                                                        <div className="dropdown">
                                                            <button
                                                                className="btn btn-sm btn-outline-secondary dropdown-toggle"
                                                                type="button"
                                                                data-bs-toggle="dropdown"
                                                            >
                                                                {t('remorques.actions.changeStatus')}
                                                            </button>
                                                            <ul className="dropdown-menu dropdown-menu-end shadow-sm border-0">
                                                                <li>
                                                                    <button className="dropdown-item d-flex align-items-center gap-2" onClick={() => handleStatusChange(remorque.id, 'AVAILABLE')}>
                                                                        <span className="p-1 bg-success rounded-circle"></span>
                                                                        {t('remorques.actions.setAvailable')}
                                                                    </button>
                                                                </li>
                                                                <li>
                                                                    <button className="dropdown-item d-flex align-items-center gap-2" onClick={() => handleStatusChange(remorque.id, 'OUT_OF_SERVICE')}>
                                                                        <span className="p-1 bg-danger rounded-circle"></span>
                                                                        {t('remorques.actions.setUnavailable')}
                                                                    </button>
                                                                </li>
                                                                <li>
                                                                    <button className="dropdown-item d-flex align-items-center gap-2" onClick={() => handleStatusChange(remorque.id, 'MAINTENANCE')}>
                                                                        <span className="p-1 bg-warning rounded-circle"></span>
                                                                        {t('remorques.actions.setInMaintenance')}
                                                                    </button>
                                                                </li>
                                                                <li>
                                                                    <button className="dropdown-item d-flex align-items-center gap-2" onClick={() => handleStatusChange(remorque.id, 'IN_USE')}>
                                                                        <span className="p-1 bg-primary rounded-circle"></span>
                                                                        {t('remorques.actions.setInUse')}
                                                                    </button>
                                                                </li>
                                                            </ul>
                                                        </div>
                                                        <button
                                                            className="btn btn-sm btn-outline-primary"
                                                            onClick={() => navigate(`/remorques/edit/${remorque.id}`)}
                                                        >
                                                            <i className="bi bi-pencil"></i>
                                                        </button>
                                                        <button
                                                            className="btn btn-sm btn-outline-danger"
                                                            onClick={() => handleDelete(remorque.id)}
                                                        >
                                                            <i className="bi bi-trash"></i>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </DashboardLayout>
    );
}

export default RemorqueList;
